import i18n from 'helpers/i18n';
import messages from './messages';
import React from 'react';
import validationSchema from './validationSchema';
import { Button, TextField, TextLink } from '@teachstonellc/component-library';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { PasswordFields } from 'components/Password';
import {
  validatePassword,
  validatePasswordConfirmation
} from 'helpers/passwordValidation';

interface SubmitHelpers {
  setFieldError: (fieldName: string, error: string) => void;
}

interface UserFormValues {
  token?: string;
  first_name: string;
  last_name: string;
  email: string;
  tos_pp_agreed_at: boolean;
  password: string;
  password_confirmation: string;
  [key: string]: unknown;
}

interface ChildRenderProps {
  register: ReturnType<typeof useForm<any>>['register'];
  errors: ReturnType<typeof useForm<any>>['formState']['errors'];
}

interface FormProps {
  /** Object used to send the data loaded by the user. */
  initialValues: UserFormValues;

  /** Yup Object used to add validations to Validation Schema. */
  validationFields?: any;

  /** Callback fired when user click on the send button. */
  handleSubmit: (values?: any, helpers?: SubmitHelpers) => void;

  /** Used to know if the page is loading. */
  loading: boolean;

  /** Used to show to the user the errors the he has. */
  errors?: string;

  /** React Node. */
  children?: React.ReactNode | ((props: ChildRenderProps) => React.ReactNode);
}

const UserForm: React.FC<FormProps> = ({
  initialValues,
  validationFields,
  handleSubmit: onFormSubmit,
  loading,
  errors,
  children
}) => {
  function getValidationSchema(): any {
    return validationFields !== undefined
      ? validationSchema.concat(validationFields)
      : validationSchema;
  }

  const baseResolver = yupResolver(getValidationSchema());

  const resolver = async (values: any, context: any, options: any) => {
    const resolved = await baseResolver(values, context, options);
    const mergedErrors = { ...resolved.errors } as any;

    const password = String(values.password || '');
    const passwordConfirmation = String(values.password_confirmation || '');

    const passwordResult = validatePassword(password, {
      firstName: String(values.first_name || ''),
      lastName: String(values.last_name || ''),
      email: String(values.email || '')
    });
    if (passwordResult !== true) {
      mergedErrors.password = { type: 'validate', message: passwordResult };
    }

    const confirmResult = validatePasswordConfirmation(
      passwordConfirmation,
      password
    );
    if (confirmResult !== true) {
      mergedErrors.password_confirmation = {
        type: 'validate',
        message: confirmResult
      };
    }

    const hasErrors = Object.keys(mergedErrors).length > 0;
    return {
      values: hasErrors ? {} : resolved.values,
      errors: mergedErrors
    };
  };

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors: formErrors }
  } = useForm<any>({
    resolver,
    defaultValues: initialValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange'
  });

  const firstName = watch('first_name', initialValues.first_name || '');
  const lastName = watch('last_name', initialValues.last_name || '');
  const email = watch('email', initialValues.email || '');

  const renderChildren =
    typeof children === 'function'
      ? children({ register, errors: formErrors })
      : children;

  function onSubmit(values: UserFormValues) {
    if (loading) {
      return;
    }

    onFormSubmit(values, {
      setFieldError: (fieldName: string, error: string) => {
        setError(fieldName, {
          type: 'server',
          message: error
        });
      }
    });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <input type="hidden" {...register('token')} />

      {renderChildren}

      <div className="grid grid-cols-1 min-[450px]:grid-cols-2 gap-4 mt-4">
        <div>
          <TextField
            {...register('first_name')}
            label={i18n.ft(messages.firstName)}
            error={formErrors.first_name?.message as string | undefined}
          />
        </div>
        <div>
          <TextField
            {...register('last_name')}
            label={i18n.ft(messages.lastName)}
            error={formErrors.last_name?.message as string | undefined}
          />
        </div>
      </div>

      <div className="mt-4">
        <TextField
          {...register('email')}
          label={i18n.ft(messages.email)}
          error={formErrors.email?.message as string | undefined}
        />
      </div>

      <div className="mt-4">
        <PasswordFields
          register={register}
          watch={watch}
          errors={formErrors as any}
          firstName={firstName}
          lastName={lastName}
          email={email}
          label={i18n.ft(messages.password)}
          confirmLabel={i18n.ft(messages.confirmPassword)}
        />

        <div className="my-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" {...register('tos_pp_agreed_at')} />
            <span>
              {i18n.ft(messages.termsOfService.privacy01)}
              <TextLink
                target="_blank"
                href="http://teachstone.com/terms-conditions/"
                rel="noopener noreferrer"
                className="[&>span]:text-[1rem] [&>span]:font-[proxima-nova]"
              >
                {i18n.ft(messages.termsOfService.privacy02)}
              </TextLink>
              {i18n.ft(messages.termsOfService.privacy03)}
              <TextLink
                target="_blank"
                href="http://teachstone.com/privacy-policy/"
                rel="noopener noreferrer"
                className="[&>span]:text-[1rem] [&>span]:font-[proxima-nova]"
              >
                {i18n.ft(messages.termsOfService.privacy04)}
              </TextLink>
            </span>
          </label>
          {formErrors.tos_pp_agreed_at?.message && (
            <div className="text-ds-text-negative text-xs mt-1">
              {formErrors.tos_pp_agreed_at.message as string}
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <Button type="submit" shape="rounded" loading={loading}>
            {i18n.ft(messages.createAccount)}
          </Button>
        </div>
      </div>
    </form>
  );
};

export default UserForm;
